import { Box, Skeleton, Theme, Typography } from "@outschool/backpack";
import { PageContainer } from "@outschool/ui-legacy-component-library";
import React from "react";

import {
  SKELETON_COLORS,
  TEACHER_DASHBOARD_SKELETON_CONTENT_BLOCKS,
  TEACHER_DASHBOARD_SKELETON_GEOMETRY,
  TEACHER_DASHBOARD_SKELETON_LAYOUT,
  TEACHER_DASHBOARD_SKELETON_SIDEBAR_SECTIONS,
} from "../../../shared/TeacherDashboardSkeletonGeometry";

const { dashboard } = TEACHER_DASHBOARD_SKELETON_GEOMETRY;
const layout = TEACHER_DASHBOARD_SKELETON_LAYOUT;

type TextVariant = "h3" | "h4" | "h5" | "body1" | "body2";

function SkeletonText({
  variant,
  widths,
}: {
  variant: TextVariant;
  widths: (number | string)[];
}) {
  return (
    <Typography variant={variant} component="div">
      {widths.map((width, index) => (
        <Skeleton key={index} variant="text" width={width} />
      ))}
    </Typography>
  );
}

function paragraphWidths(lines: number) {
  return Array.from({ length: lines }, (_, index) =>
    index === lines - 1 ? "62%" : "100%",
  );
}

function ButtonSkeleton({ width }: { width: number | string }) {
  return (
    <Skeleton
      variant="rounded"
      width={width}
      height={layout.buttonHeight}
      sx={{ borderRadius: `${layout.buttonBorderRadius}px` }}
    />
  );
}

function ContentBlockSkeleton({
  headingWidth,
  lines,
}: {
  headingWidth: string;
  lines: number;
}) {
  return (
    <Box data-test-id="teacher-dashboard-content-block-skeleton">
      <SkeletonText variant="h3" widths={[headingWidth]} />
      <Box sx={{ paddingY: `${layout.blockPaddingY}px` }}>
        <SkeletonText variant="body1" widths={paragraphWidths(lines)} />
      </Box>
    </Box>
  );
}

function ProfileCardSkeleton() {
  return (
    <Box
      data-test-id="teacher-dashboard-profile-skeleton"
      sx={{
        alignItems: "center",
        background: SKELETON_COLORS.profileGradient,
        borderRadius: `${layout.cardBorderRadius}px`,
        display: "flex",
        flexDirection: "column",
        gap: `${layout.profileGap}px`,
        padding: `${layout.cardPadding}px`,
      }}
    >
      <Skeleton
        variant="circular"
        width={layout.profileAvatarSize}
        height={layout.profileAvatarSize}
      />
      <Box sx={{ width: "100%" }}>
        <SkeletonText variant="h3" widths={["72%"]} />
        <SkeletonText variant="body1" widths={["56%"]} />
      </Box>
      <Box flex sx={{ flexWrap: "wrap", gap: "8px", justifyContent: "center" }}>
        <ButtonSkeleton width={124} />
        <ButtonSkeleton width={136} />
      </Box>
    </Box>
  );
}

function OutreachCardSkeleton() {
  return (
    <Box
      data-test-id="teacher-dashboard-outreach-skeleton"
      sx={{
        backgroundColor: SKELETON_COLORS.outreachBackground,
        borderRadius: `${layout.cardBorderRadius}px`,
        padding: `${layout.outreachPaddingY}px ${layout.outreachPaddingX}px`,
      }}
    >
      <SkeletonText variant="h5" widths={["82%", "54%"]} />
      <SkeletonText variant="body2" widths={["100%", "100%", "68%"]} />
      <SkeletonText variant="body2" widths={["72%"]} />
      <Box sx={{ marginTop: "25px", marginBottom: "5px" }}>
        <ButtonSkeleton width="100%" />
      </Box>
    </Box>
  );
}

function ResourceCardSkeleton() {
  return (
    <Box
      data-test-id="teacher-dashboard-resource-skeleton"
      sx={(theme: Theme) => ({
        backgroundColor: SKELETON_COLORS.resourceBackground,
        border: "1px solid",
        borderColor: SKELETON_COLORS.resourceBorder,
        borderRadius: `${layout.cardBorderRadius}px`,
        padding: `${layout.cardPadding}px`,
        [theme.breakpoints.up("md")]: {
          paddingBottom: `${layout.resourceBottomPadding}px`,
        },
      })}
    >
      <SkeletonText variant="h3" widths={["78%"]} />
      <SkeletonText variant="body1" widths={["100%", "86%"]} />
      <Box sx={{ marginTop: "16px" }}>
        <ButtonSkeleton width={196} />
      </Box>
    </Box>
  );
}

function SidebarSectionSkeleton({
  itemCount,
  itemLines,
}: {
  itemCount: number;
  itemLines: number;
}) {
  return (
    <Box data-test-id="teacher-dashboard-sidebar-section-skeleton">
      <SkeletonText variant="h3" widths={["46%"]} />
      <Box sx={{ display: "grid", gap: "16px", paddingY: "16px" }}>
        {Array.from({ length: itemCount }, (_unused, index) => (
          <Box
            key={index}
            sx={{ display: "grid", gap: `${layout.blockGap}px` }}
          >
            <SkeletonText variant="h4" widths={["68%"]} />
            <SkeletonText variant="body1" widths={paragraphWidths(itemLines)} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function TeacherDashboardSkeleton() {
  return (
    <PageContainer data-test-id="teacher-dashboard-skeleton" aria-busy={true}>
      <Box
        sx={(theme: Theme) => ({
          display: "grid",
          gridTemplateAreas: '"banner" "left" "right"',
          gridTemplateColumns: "auto",
          gap: `${dashboard.gap}px`,
          alignItems: "flex-start",
          [theme.breakpoints.up("md")]: {
            gridTemplateAreas: '"banner banner" "left right"',
            gridTemplateColumns: `auto ${dashboard.sidebarWidth}px`,
          },
        })}
      >
        <Box
          data-test-id="teacher-dashboard-main-skeleton"
          sx={{
            display: "grid",
            gap: `${dashboard.gap}px`,
            gridArea: "left",
          }}
        >
          {TEACHER_DASHBOARD_SKELETON_CONTENT_BLOCKS.map(
            ({ id, headingWidth, lines }) => (
              <ContentBlockSkeleton
                key={id}
                headingWidth={headingWidth}
                lines={lines}
              />
            ),
          )}
        </Box>

        <Box
          data-test-id="teacher-dashboard-sidebar-skeleton"
          sx={{
            display: "grid",
            gap: `${dashboard.gap}px`,
            gridArea: "right",
            minWidth: 0,
          }}
        >
          <ProfileCardSkeleton />
          <OutreachCardSkeleton />
          <ResourceCardSkeleton />
          {TEACHER_DASHBOARD_SKELETON_SIDEBAR_SECTIONS.map(
            ({ id, itemCount, itemLines }) => (
              <SidebarSectionSkeleton
                key={id}
                itemCount={itemCount}
                itemLines={itemLines}
              />
            ),
          )}
        </Box>
      </Box>
    </PageContainer>
  );
}
